import React, {useEffect, useState} from 'react'
import {  LoaderFunction, redirect, useLocation, useNavigate } from 'react-router-dom'
import { store } from '../../redux/store'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  Box,
  BoxProps,
  Container,
  Grid,
  Tab,
  Tabs,
  useMediaQuery,
} from '@mui/material'
import data from 'shared/assets/icons/Data.svg'
import Link from '@mui/material/Link'
import infotip from 'shared/assets/icons/Infotip.svg'
import letter from 'shared/assets/icons/Letter.svg'
import logo from 'shared/assets/icons/logos/mePrismprivacynew.svg'
import background from '../../assets/images/IndividualCreateBackground.png'
import { ThemeProvider } from '@emotion/react'
import { MpReTheme, fonts, theme } from '../../theme/OnboardingTheme'
import icon4 from 'shared/assets/icons/lucide_scan-text.svg'
import icon5 from 'shared/assets/icons/no.svg'
import { OnboardingStep1Form } from './OnboardingStep1Form'
import { OnboardingStep2Form } from './OnboardingStep2Form'
import MpTheme from 'shared/config/MpTheme'
import { useAppSelector, useAppDispatch } from '../../redux/storeExports'
import { selectActiveGrant, fetchGrants } from '../../redux/product/ProductSlice'
import { selectProfile, FetchProfile } from 'shared/redux/profile/ProfileSlice' // eslint-disable-line @typescript-eslint/no-unused-vars

// Route-level guard: runs BEFORE the onboarding component mounts. If the user
// has already completed onboarding we redirect them away so they never see the
// form. Otherwise we let them through.
export const onboardingRouteLoader: LoaderFunction = async () => {
  try {
    await Promise.all([
      store.dispatch(FetchProfile()),
      store.dispatch(fetchGrants()),
    ])
  } catch {
    // If fetching fails, fall through and let the wrapper render (it has its
    // own retry/error UX). Don't redirect on a transient failure.
    return null
  }

  const state = store.getState()
  const profile = state.profile?.profile
  const grant = selectActiveGrant(state as any)

  const onboardingStep = profile?.metadata?.onboarding_step
  const isOnboardingComplete = onboardingStep === 2

  if (!isOnboardingComplete) return null

  if (grant && !profile?.mp_auth_letter_signature) {
    return redirect('/authorization')
  }
  return redirect('/dashboard')
}
export type SignInTab = 'create' | 'sign in'
function Item(props: BoxProps) {
  const { ...other } = props
  return <Box {...other} />
}
export const AuthCopy = ({ step }: { mode: SignInTab; step?: number }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const grant = useAppSelector(selectActiveGrant)
  const showOptionalText = step === 2 && !grant
  return (
    <>
      <ThemeProvider theme={theme}>
        {!isMobile &&( <Box sx={{ px: 6, pt: 10 }}>
          <img src={logo} alt="Logo" style={{ width: 200, height: 50 }} />{' '}
        </Box>)}


        <Stack
          sx={{
            px: 10,
            pt: 10,
            pb: 4,
            '& .MuiTypography-root': {
              color: '#FFFFFF',
            },
            justifyContent: 'start',
          }}>
          <Typography
            variant={'h1'}
            sx={{
              my: 2,
              fontWeight: 'bold',
            }}>
            Create Your Priwall Profile
          </Typography>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              gridTemplateColumns: 'auto, 5fr',
              mt: { xs: 4, md: 22 }
            }}>
            <Item>
              <img
                src={data}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item>
              {' '}
              <Typography variant="body2">
                {showOptionalText ? (
                  <span style={{ color: '#EFEDFDB2' }}>
                    These fields are optional (other than city and state) though are recommended to enhance scan accuracy. They may be <span style={{ color: '#ffffff' }}>required upon subscription</span> in order to properly remove your data.
                  </span>
                ) : (
                  <span style={{ color: '#EFEDFDB2' }}>
                    Including your <span style={{ color: '#ffffff' }}>full name, city, state, and date of birth</span> helps to effectively locate your data on the web.
                  </span>
                )}
              </Typography>{' '}
            </Item>
          </Box>
          <Box
            sx={{
              display: 'flex',
              mt:3,
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              gridTemplateColumns: 'auto, 1fr',
            }}>
            <Item>
              <img
                src={letter}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item sx={{ gridColumn: 'span 5' }}>
              <Typography variant="body2">
                <span style={{ color: '#EFEDFDB2' }}>You will be <span style={{ color: '#ffffff' }}>prompted to sign</span> our authorized agent letter. This is required to <span style={{ color: '#ffffff' }}>remove your profiles</span> from some of the <span style={{ color: '#ffffff' }}>stubborn sites</span>.</span>
                
              </Typography>{' '}
            </Item>
          </Box>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              rowGap: 1,
              mt:3,
              gridTemplateColumns: 'auto, 1fr',
              alignItems: 'center',
            }}>
            <Item>
              <img
                src={infotip}
                alt="Logo"
                style={{ marginRight: 8, width: 42, height: 42 }}
              />
            </Item>
            <Item>
              <Typography variant="body2">
              Pro Tip: 

                <span style={{ marginLeft:'5px',color: '#EFEDFDB2' }}>Giving us information like previous addresses allows us to find <span style={{ color: '#ffffff' }}>more sites with your leaked data</span>. You can set up your profile with more details once you finish creating your account.</span>
             
              </Typography>
            </Item>
          </Box>
          <Box
        sx={{
          alignItems: 'left',
          marginTop: 4,
          p: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          border:
            '2px solid var(--text-colors-stroke, rgba(255, 255, 255, 0.15))',
          borderRadius: '20px',
          backdropFilter: 'blur(20.5px)',
        }}>
        <Typography variant="h4">
          Information <span style={{ color: '#C67EFF' }}>Security</span>
        </Typography>
        <Grid
  container
  spacing={4}
  sx={{
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: 4,
  }}
>
  {/* Item 1 */}
  <Grid item xs={12} md={6}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <img
        src={icon4}
        alt="Icon2"
        style={{
          verticalAlign: 'middle',
          marginRight: 8,
          width: 24,
          height: 24,
        }}
      />
      <Typography variant="body3">
        Priwall is SOC 2 compliant with industry-grade cybersecurity standards for handling sensitive data
      </Typography>
    </Box>
  </Grid>

  {/* Item 2 */}
  <Grid item xs={12} md={6}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: isMobile ? 4 : 0 }}>
      <img
        src={icon5}
        alt="Icon2"
        style={{
          verticalAlign: 'middle',
          marginRight: 8,
          width: 24,
          height: 24,
          marginLeft: 5,
        }}
      />
      <Typography variant="body3">
      We are a privacy agent. We will never share or sell the information you provide to us. All the data you provide during onboarding is used for the
      sole expressed purpose of finding and removing your information.
      </Typography>
    </Box>
  </Grid>
</Grid>


      </Box>
        </Stack>
      </ThemeProvider>
    </>
  )
}
export const PrivacyOnboardingWrapper = () => {
  const location = useLocation()
  const isWide = useMediaQuery(theme.breakpoints.up('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const textStyle = {
    ...fonts.Inter.Regular,
    fontSize: '16px',
  }
  const mode: SignInTab = location.pathname.includes('create')
    ? 'create'
    : 'sign in'
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const grant = useAppSelector(selectActiveGrant)
  const profile = useAppSelector(selectProfile)
  const [step, setStep] = useState<1 | 2>(() =>
    profile?.metadata?.onboarding_step === 1 ? 2 : 1,
  )

  const [isLoading, setIsLoading] = useState(true)

  const onboardingStep = profile?.metadata?.onboarding_step
  const isOnboardingComplete = onboardingStep === 2

  useEffect(() => {
    if (isOnboardingComplete) {
      if (grant && !profile?.mp_auth_letter_signature) {
        navigate('/authorization', { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    } else if (onboardingStep === 1) {
      setStep(2)
    }
  }, [
    isOnboardingComplete,
    onboardingStep,
    profile?.mp_auth_letter_signature,
    grant,
    navigate,
  ])

  // Fetch grants and profile when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true)
        await Promise.all([dispatch(fetchGrants()), dispatch(FetchProfile())])
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      } finally {
        setIsLoading(false)
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false} sx={{ background: '#1A1B20', minHeight: '100vh', position: 'relative' }}>
        <Grid container>
          <Grid item xs={12} md={5} sx={{ minHeight: { md: '100vh' }, position: 'relative', display: { md: 'flex' }, flexDirection: { md: 'column' } }}>
            <Box sx={{ pb: { xs: 8, md: 12 }, flex: { md: 1 } }}>
         {!isWide &&(<Box sx={{ px: 1, pt: 10 }}>
          <img src={logo} alt="Logo" style={{ width: 300, height: 75, objectFit: 'contain' }} />{' '}
        </Box>)}
            {!isWide ? (
              // Only show tabs when not loading
              !isLoading ? (
                <Tabs
                  sx={{ px: 3, pt: 10,".MuiTab-root":{ alignItems: "start", },  }}
                  value={2}
                  indicatorColor="secondary"
                  textColor="primary"
                  centered={false}>
                  <Tab
                  style={{minWidth:"20%"}}
                    label="1."
                    sx={{
                      flexGrow: 0,
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  <Tab
                      label="2."
                      style={{minWidth:"20%"}}
                      sx={{ 
                        flexGrow: 0,
                        margin:'0 2px',
                        textTransform: 'none',
                        fontFamily: fonts.Inter.SemiBold,
                      }}
                  />
                  <Tab
                    label="3.Set Profile"
                    sx={{
                      width:'40%', 
                      color: 'inherit',  
                      '&.Mui-selected': {
                        color: MpReTheme.colors.tertiary.pink.default,
                      },
                      flexGrow: 1,
                      margin:'0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  {!!grant && (
                  <Tab
                    label="4."
                    style={{minWidth:"20%"}}
                    sx={{
                      flexGrow: 0,
                      margin:'0 2px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                    }}
                  />
                  )}
                </Tabs>
              ) : (
                // Show a loading indicator or placeholder when loading
                <Box sx={{ px: 3, pt: 10, height: '48px' }} />
              )
            ) : (
              // Only show tabs when not loading
              !isLoading ? (
                <Tabs
                  sx={{ px: 3, pt: 10,".MuiTab-root":{ alignItems: "start", }, }}
                  value={2}
                  indicatorColor="secondary"
                  textColor="primary"
                  centered>
                  <Tab
                    label="1.Sign Up"
                    sx={{
                      margin:'0 5px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                      borderBottom: '2px solid #EFEDFDB2',
                    }}
                  />
                  <Tab
                      label="2.Verify"
                      sx={{
                        margin:'0 5px',
                        textTransform: 'none',
                        fontFamily: fonts.Inter.SemiBold,
                        borderBottom: '2px solid #EFEDFDB2',
                      }}
                  />
                  <Tab
                  label="3.Set Profile"
                    sx={{
                      margin:'0 5px',
                      textTransform: 'none',
                      fontFamily: fonts.Inter.SemiBold,
                      color: 'inherit',
                      '&.Mui-selected': {
                        color: MpReTheme.colors.tertiary.pink.default,
                      },
                    }}
                  />
                  {!!grant && (
                    <Tab
                      label="4.Authorization"
                      sx={{
                        margin:'0 5px',
                        textTransform: 'none',
                        fontFamily: fonts.Inter.SemiBold,
                        borderBottom: '2px solid #EFEDFDB2',
                      }}
                    />
                  )}
                </Tabs>
              ) : (
                // Show a loading indicator or placeholder when loading
                <Box sx={{ px: 3, pt: 10, height: '48px' }} />
              )
            )}
            {step === 1 ? (
              <OnboardingStep1Form onNext={() => setStep(2)} grant={!!grant} isUpdate={(profile?.metadata?.onboarding_step ?? 0) >= 1} />
            ) : (
              <OnboardingStep2Form grant={grant} onBack={() => setStep(1)} />
            )}
            </Box>
            <Box
              sx={{
                ...(isDesktop ? { mb: 12 } : { mt: 9, mb: 11 }),
                maxWidth: MpTheme.layouts.widths.sm,
                mx: 'auto',
                px: isDesktop ? 3 : 0,
                width: '100%',
              }}>
              <Stack
                direction="row"
                sx={{ alignItems: 'center', justifyContent: isDesktop ? 'space-between' : 'flex-start', ml: isDesktop ? 0 : 3.8 }}
                spacing={isDesktop ? 15 : 20}>
                <Link
                  href="https://meprism.com/terms-conditions"
                  sx={{ ...textStyle, color: MpReTheme.colors.secondary.default }}>
                  Terms & Conditions
                </Link>
                <Link
                  href="https://meprism.com/privacy-policy"
                  sx={{ ...textStyle, color: MpReTheme.colors.secondary.default }}>
                  Privacy Policy
                </Link>
                <Link
                  href="https://meprism1.atlassian.net/servicedesk/customer/portal/1/group/1/create/17"
                  sx={{ ...textStyle, color: MpReTheme.colors.secondary.default }}>
                  Contact Us
                </Link>
              </Stack>
            </Box>
           
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              minHeight: { md: '100vh' },
              backgroundImage: `url(${background})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              display: { md: 'flex' },
              flexDirection: { md: 'column' },
            }}>
              <Box sx={{ position: 'relative', minHeight: { md: '100vh' }, flex: { md: 1 }, display: { md: 'flex' }, flexDirection: { md: 'column' } }}>
            <Box sx={{ flex: { md: 1 } }}>
              <AuthCopy mode={mode} step={step} />
            </Box>
            <Box sx={isDesktop ? { mb: 12 } : { pb: 4 }}>
            <Typography variant="body4" sx={{ mx: 10 }}>
              Are you a Business?
              <Link
                href={
                  'https://meprism1.atlassian.net/servicedesk/customer/portal/1/group/1/create/17'
                }
                style={{ textDecoration: 'underline', fontSize: '16px' }}
                sx={{
                  ml:2,
                  ...textStyle,
                  color: MpReTheme.colors.tertiary.pink.default,
                }}>
                Contact Us
              </Link>
            </Typography></Box></Box>
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  )
}
